import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { formatHoursDecimal as fmtH } from '@/lib/formatHours';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertTriangle, MapPin, Link2, Loader2, AlarmClockOff, Clock, User,
} from 'lucide-react';

// P-FLOAT — "Pointages non assignés" tab. Lists floating field_activities
// (client_id = null) and lets the admin EITHER:
//   • Attach a CLOSED floating activity to a worksite (existing flow)
//   • Force-close an OPEN ghost activity with an explicit clock-out
//     timestamp + reason (new flow — fixes the "Already clocked in
//     (floating)" prod incident).
// API:
//   GET  /api/mobile/admin/unassigned-activities
//   POST /api/mobile/admin/activity/:id/attach          body: { client_id }
//   POST /api/mobile/admin/activity/:id/force-close     body: { clock_out_iso, reason }

const hoursBetween = (clockIn, clockOut) => {
  if (!clockIn || !clockOut) return 0;
  const tIn = new Date(clockIn).getTime();
  const tOut = new Date(clockOut).getTime();
  if (Number.isNaN(tIn) || Number.isNaN(tOut)) return 0;
  return Math.max((tOut - tIn) / 3600000, 0);
};

const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('fr-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
};

// Convert ISO → "YYYY-MM-DDTHH:mm" for <input type="datetime-local">.
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};

// Convert "YYYY-MM-DDTHH:mm" (local TZ) → full ISO with local offset preserved.
const localInputToIso = (val) => {
  if (!val) return '';
  // Browser interprets the value as LOCAL time. new Date(val) does the same.
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
};

export default function UnassignedActivitiesTab({ tf }) {
  const [activities, setActivities] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selections, setSelections] = useState({}); // activity_id -> client_id
  const [busyId, setBusyId] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Refresh "duration ongoing" every 30s so the live counter ticks.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const [closeDialog, setCloseDialog] = useState(null); // {activity, clockOutLocal, reason}
  const [confirmClose, setConfirmClose] = useState(false);

  const loadActivities = useCallback(async () => {
    try {
      const { data } = await api.get('/mobile/admin/unassigned-activities');
      setActivities(data.activities || []);
    } catch {
      toast.error(tf('Erreur de chargement', 'Loading error'));
    }
  }, [tf]);

  const loadClients = useCallback(async () => {
    try {
      const { data } = await api.get('/clients?limit=500');
      setClients(data.clients || []);
    } catch {
      /* non-blocking */
    }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadActivities(), loadClients()]);
      setLoading(false);
    })();
  }, [loadActivities, loadClients]);

  const attach = async (activityId) => {
    const clientId = selections[activityId];
    if (!clientId) {
      toast.error(tf('Sélectionnez un chantier', 'Pick a worksite'));
      return;
    }
    setBusyId(activityId);
    try {
      await api.post(`/mobile/admin/activity/${activityId}/attach`, { client_id: clientId });
      toast.success(tf('Pointage rattaché', 'Punch attached'));
      setActivities((prev) => prev.filter((a) => a.activity_id !== activityId));
      setSelections((prev) => {
        const { [activityId]: _drop, ...rest } = prev;
        return rest;
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : tf('Erreur de rattachement', 'Attach error'));
    } finally {
      setBusyId(null);
    }
  };

  const openCloseDialog = (a) => {
    // Pre-populate with the clock-in time itself so the admin is forced
    // to think about the right value — we DO NOT default to now.
    setCloseDialog({
      activity: a,
      clockOutLocal: isoToLocalInput(a.clock_in),
      reason: '',
    });
    setConfirmClose(false);
  };

  const submitForceClose = async () => {
    if (!closeDialog) return;
    const { activity, clockOutLocal, reason } = closeDialog;
    const clockOutIso = localInputToIso(clockOutLocal);
    if (!clockOutIso) {
      toast.error(tf('Date/heure de sortie requise', 'Clock-out date/time required'));
      return;
    }
    const inMs = new Date(activity.clock_in).getTime();
    const outMs = new Date(clockOutIso).getTime();
    if (Number.isNaN(outMs) || outMs <= inMs) {
      toast.error(tf("L'heure de sortie doit être après l'entrée",
        'Clock-out must be strictly after clock-in'));
      return;
    }
    if (reason.trim().length < 3) {
      toast.error(tf("Motif requis (min. 3 caractères)", 'Reason required (>= 3 chars)'));
      return;
    }
    if (!confirmClose) {
      toast.error(tf('Cochez la case de confirmation', 'Tick the confirmation box'));
      return;
    }
    setBusyId(activity.activity_id);
    try {
      await api.post(`/mobile/admin/activity/${activity.activity_id}/force-close`, {
        clock_out_iso: clockOutIso,
        reason: reason.trim(),
      });
      toast.success(tf('Pointage clôturé', 'Punch closed'));
      // Refresh the list — the row now becomes a "closed floating" and
      // STAYS in the unassigned-tab so the admin can still attach it
      // to a client retroactively.
      await loadActivities();
      setCloseDialog(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : tf('Erreur de clôture', 'Close error'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!activities.length) {
    return (
      <Card data-testid="unassigned-empty">
        <CardContent className="py-12 text-center text-muted-foreground">
          <AlertTriangle className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>{tf('Aucun pointage sans chantier', 'No floating punches')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="unassigned-list">
      {activities.map((a) => {
        const isOpen = !a.clock_out;
        const live = isOpen
          ? Math.max((now - new Date(a.clock_in).getTime()) / 3600000, 0)
          : hoursBetween(a.clock_in, a.clock_out);
        return (
          <Card key={a.activity_id} data-testid={`unassigned-row-${a.activity_id}`}
            className={isOpen ? 'border-orange-300 bg-orange-50/40' : ''}>
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold" data-testid={`row-employee-${a.activity_id}`}>
                    {a.employee_name || a.employee_id}
                  </span>
                  <Badge variant="secondary" className="text-xs" data-testid={`row-date-${a.activity_id}`}>
                    {a.date}
                  </Badge>
                  <Badge className="text-xs bg-purple-100 text-purple-800 border border-purple-300">
                    {tf('Pointage flottant', 'Floating punch')}
                  </Badge>
                  {isOpen ? (
                    <Badge className="text-xs bg-orange-500 text-white animate-pulse"
                      data-testid={`row-open-${a.activity_id}`}>
                      {tf('En cours', 'In progress')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">{fmtH(live)}</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {fmtDateTime(a.clock_in)}
                  {a.clock_out ? ` → ${fmtDateTime(a.clock_out)}` : ''}
                </div>
              </div>
              {isOpen && (
                <div className="text-sm bg-orange-100 border border-orange-300 text-orange-800 rounded px-3 py-2"
                  data-testid={`row-duration-${a.activity_id}`}>
                  {tf('Durée actuelle :', 'Current duration:')} <strong>{fmtH(live)}</strong>
                  {' — '}
                  {tf("ce pointage est ouvert et bloque tout nouveau clock-in de l'employé.",
                    'this punch is open and blocks any new clock-in for the employee.')}
                </div>
              )}
              {a.clock_in_location && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  GPS in: {a.clock_in_location.lat?.toFixed?.(4)}, {a.clock_in_location.lng?.toFixed?.(4)}
                </div>
              )}
              {a.force_closed_by && (
                <div className="text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1 text-blue-800">
                  <strong>{tf('Clôture administrative', 'Administrative close')}</strong>
                  {' — '}{tf('par', 'by')} {a.force_closed_by}
                  {' · '}{tf('motif', 'reason')}: {a.force_close_reason}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {!isOpen && (
                  <>
                    <Select
                      value={selections[a.activity_id] || ''}
                      onValueChange={(v) => setSelections((p) => ({ ...p, [a.activity_id]: v }))}
                    >
                      <SelectTrigger className="w-[280px]"
                        data-testid={`attach-select-${a.activity_id}`}>
                        <SelectValue placeholder={tf('Choisir un chantier', 'Pick a worksite')} />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((c) => (
                          <SelectItem key={c.client_id} value={c.client_id}>
                            {`${c.first_name || ''} ${c.last_name || ''}`.trim() || c.client_id}
                            {c.city ? ` — ${c.city}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={() => attach(a.activity_id)}
                      disabled={busyId === a.activity_id}
                      data-testid={`attach-btn-${a.activity_id}`}
                    >
                      {busyId === a.activity_id
                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <Link2 className="h-4 w-4 mr-1" />}
                      {tf('Rattacher', 'Attach')}
                    </Button>
                  </>
                )}
                {isOpen && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => openCloseDialog(a)}
                    data-testid={`force-close-btn-${a.activity_id}`}
                  >
                    <AlarmClockOff className="h-4 w-4 mr-1" />
                    {tf('Clôturer le pointage', 'Close punch')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={!!closeDialog} onOpenChange={(o) => !o && setCloseDialog(null)}>
        <DialogContent data-testid="force-close-dialog">
          <DialogHeader>
            <DialogTitle>{tf('Clôturer un pointage ouvert', 'Close an open punch')}</DialogTitle>
            <DialogDescription>
              {tf(
                "Spécifiez l'heure exacte à laquelle l'employé est sorti. Aucune valeur n'est pré-remplie avec l'heure actuelle pour éviter de fausser les heures travaillées.",
                'Specify the exact time the employee left. No value is auto-filled with the current time to avoid falsifying worked hours.',
              )}
            </DialogDescription>
          </DialogHeader>
          {closeDialog && (
            <div className="space-y-3">
              <div className="text-sm bg-slate-50 text-slate-900 rounded px-3 py-2 space-y-1">
                <div><strong>{tf('Employé', 'Employee')}:</strong> {closeDialog.activity.employee_name || closeDialog.activity.employee_id}</div>
                <div><strong>{tf('Entrée', 'Clock-in')}:</strong> {fmtDateTime(closeDialog.activity.clock_in)}</div>
                <div><strong>Activity ID:</strong> <code className="text-xs">{closeDialog.activity.activity_id}</code></div>
              </div>
              <div>
                <Label htmlFor="fc-out" className="text-sm">
                  {tf('Heure de sortie *', 'Clock-out time *')}
                </Label>
                <Input
                  id="fc-out"
                  type="datetime-local"
                  value={closeDialog.clockOutLocal}
                  onChange={(e) => setCloseDialog((p) => ({ ...p, clockOutLocal: e.target.value }))}
                  data-testid="force-close-clockout-input"
                />
              </div>
              <div>
                <Label htmlFor="fc-reason" className="text-sm">
                  {tf('Motif (correction administrative) *', 'Reason (administrative correction) *')}
                </Label>
                <Textarea
                  id="fc-reason"
                  rows={3}
                  placeholder={tf(
                    "Ex : oubli du clock-out, panne réseau, batterie vide…",
                    'Eg: forgot clock-out, network outage, dead battery…',
                  )}
                  value={closeDialog.reason}
                  onChange={(e) => setCloseDialog((p) => ({ ...p, reason: e.target.value }))}
                  data-testid="force-close-reason-input"
                />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confirmClose}
                  onChange={(e) => setConfirmClose(e.target.checked)}
                  data-testid="force-close-confirm"
                  className="mt-1"
                />
                <span>
                  {tf(
                    "Je confirme avoir validé l'heure de sortie ci-dessus avec l'employé (ou via un justificatif).",
                    'I confirm I have validated the clock-out time above with the employee (or via a justification).',
                  )}
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialog(null)}>
              {tf('Annuler', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={submitForceClose}
              disabled={busyId === closeDialog?.activity?.activity_id}
              data-testid="force-close-submit"
            >
              {busyId === closeDialog?.activity?.activity_id
                ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                : <AlarmClockOff className="h-4 w-4 mr-1" />}
              {tf('Confirmer la clôture', 'Confirm close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
